import { create } from 'zustand';
import type { ReactNode } from 'react';
import { persistToast } from '@/services/toast-persistence.service';

export type ToastType = 'success' | 'error' | 'info' | 'warning';
export type ToastVariant = 'standard' | 'rich' | 'action';
export type ToastPosition = 'top-right' | 'bottom-left' | 'bottom-right';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastActionButton {
  label: string;
  onClick: () => void | Promise<void>;
  variant?: 'primary' | 'success' | 'danger' | 'ghost';
  loadingLabel?: string;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  action?: ToastAction;
  variant?: ToastVariant;
  title?: string;
  body?: string;
  priority?: 'urgent' | 'high' | 'normal' | 'low';
  actions?: ToastActionButton[];
  position?: ToastPosition;
  onClick?: () => void;
  icon?: string;
  requiresManualDismiss?: boolean;
  component?: ReactNode;
  persist?: boolean;
}

const PRIORITY_DURATIONS: Record<string, number> = {
  urgent: 15000,
  high: 10000,
  normal: 5000,
  low: 3000,
};

function getEffectiveDuration(toast: Omit<Toast, 'id'>): number | null {
  if (toast.requiresManualDismiss) return null;
  if (toast.duration > 0) return toast.duration;
  if (toast.priority && PRIORITY_DURATIONS[toast.priority]) {
    return PRIORITY_DURATIONS[toast.priority];
  }
  return null;
}

interface ToastState {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (message: string, duration?: number, action?: ToastAction) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
}

let toastId = 0;

const MAX_TOASTS = 20;
const DEDUP_WINDOW_MS = 2000;

/** Track recent toasts for deduplication: key -> timestamp */
const recentToasts = new Map<string, number>();

function dedupeKey(toast: Omit<Toast, 'id'>): string {
  return `${toast.title ?? ''}::${toast.message}`;
}

export const useToastStore = create<ToastState>((set) => {
  const addToast = (toast: Omit<Toast, 'id'>) => {
    // Deduplication: skip if identical title+message was added in last 2 seconds
    const key = dedupeKey(toast);
    const now = Date.now();
    const lastSeen = recentToasts.get(key);
    if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) {
      return;
    }
    recentToasts.set(key, now);

    // Persist to notification history if flagged or if error/warning type
    const shouldPersist = toast.persist === true || ((toast.type === 'error' || toast.type === 'warning') && toast.persist !== false);
    if (shouldPersist) {
      const priorityMap: Record<string, 'low' | 'normal' | 'high' | 'urgent'> = {
        error: 'high', warning: 'normal', info: 'normal', success: 'low',
      };
      persistToast({
        title: toast.title || toast.message,
        body: toast.body || toast.message,
        priority: toast.priority || priorityMap[toast.type] || 'normal',
      });
    }

    const id = `toast_${++toastId}`;
    const toastWithDefaults = {
      ...toast,
      variant: toast.variant ?? ('standard' as ToastVariant),
      // Default to bottom-right so toasts never overlap modal headers / close (X) buttons.
      position: toast.position ?? ('bottom-right' as ToastPosition),
      id,
    };
    set((state) => {
      let toasts = [...state.toasts, toastWithDefaults];
      // Enforce max limit: remove oldest non-action toasts first
      while (toasts.length > MAX_TOASTS) {
        const removeIdx = toasts.findIndex((t) => !t.actions?.length && !t.action);
        if (removeIdx >= 0) {
          toasts.splice(removeIdx, 1);
        } else {
          // All toasts have actions; remove the oldest anyway
          toasts.shift();
        }
      }
      return { toasts };
    });

    const effectiveDuration = getEffectiveDuration(toast);
    if (effectiveDuration !== null && effectiveDuration > 0) {
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      }, effectiveDuration);
    }
  };

  return {
    toasts: [],
    addToast,
    removeToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
    success: (message, duration = 4000, action?) => addToast({ type: 'success', message, duration, action }),
    error: (message, duration = 6000) => addToast({ type: 'error', message, duration }),
    info: (message, duration = 4000) => addToast({ type: 'info', message, duration }),
    warning: (message, duration = 5000) => addToast({ type: 'warning', message, duration }),
  };
});
